import type { PriceUnitDto } from '@/api';
import {
  formatIndex,
  formatPriceField,
} from '@/components/table/table-format-helpers';
import type {
  HarmonizationPivotRow,
  HarmonizationPivotRowForDisplay,
  PriceListData,
  PricelistSettings,
  RowData,
} from '../../utils/simulation-types';

export const formatHarmonizationDataForDisplay = (
  data: HarmonizationPivotRow[],
): HarmonizationPivotRowForDisplay[] =>
  data.map((row) => ({
    ...row,
    priceListData: Object.fromEntries(
      Object.entries(row.priceListData).map(([priceListId, values]) => [
        priceListId,
        {
          localRrp: formatPriceField(values.localRrp),
          localLp: formatPriceField(values.localLp),
          localNip: formatPriceField(values.localNip),
          altRrpIndex: formatIndex(values.altRrpIndex),
          altLpIndex: formatIndex(values.altLpIndex),
          altNipIndex: formatIndex(values.altNipIndex),
        },
      ]),
    ),
  }));

const groupByProduct = (data: RowData[]): Map<number, Map<number, RowData>> => {
  const grouped = new Map<number, Map<number, RowData>>();

  for (const row of data) {
    let productMap = grouped.get(row.productId);
    if (!productMap) {
      productMap = new Map<number, RowData>();
      grouped.set(row.productId, productMap);
    }
    productMap.set(row.priceUnitId, row);
  }

  return grouped;
};

const calculateAltIndex = (
  localValue: number,
  localFxRate: number,
  altValue: number | null,
  altFxRate: number,
): number => {
  if (
    altValue === null ||
    altValue === 0 ||
    altFxRate === 0 ||
    localFxRate === 0
  ) {
    return 0;
  }

  const localInLLCurrency = localValue / localFxRate;
  const altInLLCurrency = altValue / altFxRate;

  if (altInLLCurrency === 0) return 0;

  return (localInLLCurrency / altInLLCurrency) * 100;
};

export const transformToHarmonizationPivot = (
  data: RowData[],
  priceLists: PriceUnitDto[],
  alternativePriceListId: number,
  pricelistSettings: Map<string, PricelistSettings>,
): HarmonizationPivotRow[] => {
  if (data.length === 0 || priceLists.length === 0) {
    return [];
  }

  const fxRateCache = new Map<number, number>();
  for (const pl of priceLists) {
    const settings = pricelistSettings.get(pl.id.toString());
    fxRateCache.set(pl.id, settings?.alternativeFxRate ?? 1);
  }

  const groupedByProduct = groupByProduct(data);

  const altFxRate = fxRateCache.get(alternativePriceListId) ?? 1;
  const pivotRows: HarmonizationPivotRow[] = [];

  for (const [productId, productRowsMap] of groupedByProduct) {
    const firstRow = productRowsMap.values().next().value;
    if (!firstRow) continue;

    const altRow = productRowsMap.get(alternativePriceListId) ?? null;

    const priceListData: Record<number, PriceListData> = {};

    for (const priceList of priceLists) {
      const priceListRow = productRowsMap.get(priceList.id);
      const localFxRate = fxRateCache.get(priceList.id) ?? 1;

      const plData: PriceListData = {
        localRrp: priceListRow?.rrp ?? 0,
        localLp: priceListRow?.lp ?? 0,
        localNip: priceListRow?.nip ?? 0,
        altRrpIndex: 0,
        altLpIndex: 0,
        altNipIndex: 0,
      };

      if (priceListRow) {
        plData.altRrpIndex = calculateAltIndex(
          priceListRow.rrp,
          localFxRate,
          altRow?.rrp ?? null,
          altFxRate,
        );
        plData.altLpIndex = calculateAltIndex(
          priceListRow.lp,
          localFxRate,
          altRow?.lp ?? null,
          altFxRate,
        );
        plData.altNipIndex = calculateAltIndex(
          priceListRow.nip,
          localFxRate,
          altRow?.nip ?? null,
          altFxRate,
        );
      }

      priceListData[priceList.id] = plData;
    }

    const pivotRow: HarmonizationPivotRow = {
      themeName: firstRow.themeName,
      productId: firstRow.productId,
      productName: firstRow.productName,
      launchStatus:
        typeof firstRow.launchStatus === 'string'
          ? firstRow.launchStatus
          : String(firstRow.launchStatus),
      priceListData,
    };

    pivotRows.push(pivotRow);
  }

  return pivotRows;
};
